import { useEffect, useState } from "react";

const processedAssets = new Map<string, Promise<string>>();

type Props = {
  source: string;
  alt: string;
  className: string;
  placeholder: string;
};

type ProcessedImage = {
  url: string;
  alt: string;
};

function rgbFromCss(variable: string, fallback: [number, number, number]) {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(variable)
    .match(/\d+(?:\.\d+)?/g);
  return value && value.length >= 3
    ? ([Number(value[0]), Number(value[1]), Number(value[2])] as [
        number,
        number,
        number,
      ])
    : fallback;
}

function loadProcessedAsset(source: string): Promise<string> {
  const existing = processedAssets.get(source);
  if (existing) return existing;

  const task = new Promise<string>((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) {
        reject(new Error("Canvas is unavailable"));
        return;
      }
      context.drawImage(image, 0, 0);
      const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
      const background = rgbFromCss("--black", [0, 0, 0]);
      const accent = rgbFromCss("--accent", [0, 255, 102]);
      for (let index = 0; index < pixels.data.length; index += 4) {
        const tone =
          (pixels.data[index] * 0.299 +
            pixels.data[index + 1] * 0.587 +
            pixels.data[index + 2] * 0.114) /
          255;
        pixels.data[index] = Math.round(
          background[0] + (accent[0] - background[0]) * tone,
        );
        pixels.data[index + 1] = Math.round(
          background[1] + (accent[1] - background[1]) * tone,
        );
        pixels.data[index + 2] = Math.round(
          background[2] + (accent[2] - background[2]) * tone,
        );
      }
      context.putImageData(pixels, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    image.onerror = () => reject(new Error("Image loading failed"));
    image.src = source;
  });
  processedAssets.set(source, task);
  task.catch(() => processedAssets.delete(source));
  return task;
}

export function PipboyProcessedImage({
  source,
  alt,
  className,
  placeholder,
}: Props) {
  const [processed, setProcessed] = useState<ProcessedImage | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadProcessedAsset(source)
      .then((result) => {
        if (!cancelled) setProcessed({ url: result, alt });
      })
      .catch(() => {
        if (!cancelled) setProcessed(null);
      });
    return () => {
      cancelled = true;
    };
  }, [alt, source]);

  if (!processed) {
    return <span aria-hidden="true">{placeholder}</span>;
  }
  return <img className={className} src={processed.url} alt={processed.alt} />;
}
